import { Select as MuiSelect, MenuItem, Theme, ListSubheader, Box, Stack } from '@mui/material';
import { ChangeEvent, FC } from 'react';
import FormControl from '../form-control';
import { FormProps, getMuiSize } from '../helpers';
import { UiOption } from '@/lib/helpers';
import { createUseStyles } from 'react-jss';
import Text from '@/components/text';

interface Props extends FormProps {
  value: string;
  options: UiOption[];
  onChange: (value: string, event: ChangeEvent<HTMLInputElement>) => void;
}

const useStyles = createUseStyles((theme: Theme) => ({
  select: {
    backgroundColor: theme.palette.common.white,
    '& fieldset': {
      borderWidth: '1px !important',
    },
    '&.Mui-disabled fieldset': {
      borderColor: `${theme.palette.darkGray} !important`,
      backgroundColor: '#dadada1a',
    },
  },
  subheader: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    lineHeight: '30px',
    marginTop: 10,
    paddingLeft: 8,
  },
  menuItem: {
    whiteSpace: 'wrap',
  },
}));

const Select: FC<Props> = ({
  name,
  value = '',
  options,
  label = '',
  disabled = false,
  autoFocus = false,
  onChange,
  error = '',
  size = 'medium',
  fullWidth = false,
  readonly = false,
  required = false,
}) => {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange(String(event.target.value), event);

  const labelId = `${name}-label`;
  const styles = useStyles();
  const labelFormatted = required && typeof label === 'string' ? `${label} *` : label;

  return (
    <FormControl label={labelFormatted} error={error} name={name} size={size}>
      <MuiSelect
        labelId={labelId}
        id={name}
        name={name}
        value={value}
        label={labelFormatted}
        onChange={handleChange}
        disabled={disabled || readonly}
        autoFocus={autoFocus}
        error={!!error}
        size={getMuiSize(size)}
        fullWidth={fullWidth}
        className={styles.select}
        required={required}
        renderValue={(selected) => {
          const option = options.find(({ value }) => value === selected);
          return option?.label || '';
        }}
        MenuProps={{
          PaperProps: {
            style: {
              maxWidth: 200,
              width: '100%',
            },
          },
        }}
      >
        {options.map(({ value, label, description, disabled = false, meta = {}, key }) => {
          if (meta.header) {
            return (
              <ListSubheader className={styles.subheader} key={key || value}>
                {label}
              </ListSubheader>
            );
          }

          const hasDescription = !!description;

          return (
            <MenuItem
              key={key || value}
              value={value}
              disabled={disabled}
              className={styles.menuItem}
            >
              <Stack width="100%">
                <Box>
                  <Text color="black" dotdot>
                    {label}
                  </Text>
                </Box>
                {hasDescription && (
                  <Box mt={0.5}>
                    <Text size="small" color="grey">
                      {description}
                    </Text>
                  </Box>
                )}
              </Stack>
            </MenuItem>
          );
        })}
      </MuiSelect>
    </FormControl>
  );
};

export default Select;
